import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { MAX_NAMES, VOICE_LABELS, buildJobs, columnsFor, parseNames, summarize, toCsv } from "@/lib/voiceCheck";

// Clips made at the same time; keeps the page responsive and the voice service happy.
const CONCURRENCY = 3;

function Button({ children, variant = "secondary", ...props }) {
  const cls = variant === "primary"
    ? "bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white border-[color:var(--brand)] disabled:opacity-50"
    : "border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] disabled:opacity-50";
  return (
    <button type="button" className={`rounded-full border px-3 py-1 text-xs font-medium ${cls}`} {...props}>
      {children}
    </button>
  );
}

function StepTitle({ number, title, children }) {
  return (
    <div>
      <h2 className="text-sm font-semibold">
        <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--brand)] text-[11px] text-white">{number}</span>
        {title}
      </h2>
      {children && <p className="text-xs text-[color:var(--text-secondary)] mt-1">{children}</p>}
    </div>
  );
}

function toObjectUrl(base64, mime) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime || "audio/mpeg" }));
}

// Plays one clip from the API through a shared <audio>; returns once it starts.
async function playClip(audioEl, data) {
  const url = toObjectUrl(data.audio_base64, data.mime);
  if (!audioEl) return;
  audioEl.src = url;
  audioEl.onended = () => URL.revokeObjectURL(url);
  await audioEl.play();
}

// ── Step 1: the global interview voice ─────────────────────────────────────

function InterviewVoiceCard({ saved, setSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (saved) setDraft({ voices: { ...saved.voices }, instructions: saved.instructions });
  }, [saved]);

  if (!saved || !draft) return null;
  const dirty = draft.voices.female !== saved.voices.female
    || draft.voices.male !== saved.voices.male
    || draft.instructions !== saved.instructions;
  const isDefault = draft.voices.female === saved.defaults.voices.female
    && draft.voices.male === saved.defaults.voices.male
    && draft.instructions === saved.defaults.instructions;

  const preview = async (voice) => {
    setPreviewing(voice);
    try {
      const { data } = await api.post("/admin/interview-voice/preview", {
        voice, voices: draft.voices, instructions: draft.instructions,
      }, { timeout: 45000 });
      await playClip(audioRef.current, data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPreviewing(null);
    }
  };

  const save = async () => {
    if (!window.confirm("Change the interview voice for all new interview audio? Saved question audio will need regenerating.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/interview-voice", draft);
      setSaved(data);
      setJustSaved(true);
      toast.success("Interview voice saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4" data-testid="interview-voice-card">
      <StepTitle number={1} title="Interview voice">
        The interviewer's voice for all interview audio: questions, follow-up questions, the welcome and the phrases between questions.
        Preview your choices, then save. Steps 2 and 3 use the saved voice.
      </StepTitle>
      {saved.provider === "elevenlabs" && (
        <p className="text-xs text-amber-700" data-testid="interview-voice-elevenlabs-note">
          The interview voice is ElevenLabs now (step 1b). These OpenAI settings are used only if you switch back.
        </p>
      )}
      <div className="flex flex-wrap gap-6">
        {["female", "male"].map((g) => (
          <div key={g} className="flex items-end gap-2">
            <label className="text-xs font-medium">
              {VOICE_LABELS[g]} voice
              <select
                value={draft.voices[g]}
                onChange={(e) => setDraft((d) => ({ ...d, voices: { ...d.voices, [g]: e.target.value } }))}
                className="mt-1 block rounded-lg border border-[color:var(--border-stone)] px-2 py-1 text-sm font-normal"
                data-testid={`interview-voice-${g}`}
              >
                {saved.available_voices.map((v) => (
                  <option key={v} value={v}>{v}{v === saved.defaults.voices[g] ? " (default)" : ""}</option>
                ))}
              </select>
            </label>
            <Button onClick={() => preview(g)} disabled={Boolean(previewing) || !draft.instructions.trim()}>
              {previewing === g ? "Making…" : "▶ Preview"}
            </Button>
          </div>
        ))}
      </div>
      <label className="block text-xs font-medium">
        Tone: how the interviewer should speak
        <textarea
          value={draft.instructions}
          onChange={(e) => setDraft((d) => ({ ...d, instructions: e.target.value }))}
          maxLength={saved.max_chars}
          rows={4}
          className="mt-1 w-full rounded-lg border border-[color:var(--border-stone)] p-2 text-sm font-normal"
          data-testid="interview-voice-instructions"
        />
      </label>
      <p className="text-[11px] text-[color:var(--text-secondary)]">Preview says: <em>"{saved.sample_question}"</em></p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={save} disabled={saving || !dirty || !draft.instructions.trim()} data-testid="interview-voice-save">
          {saving ? "Saving…" : "Save interview voice"}
        </Button>
        <Button onClick={() => setDraft({ voices: { ...saved.defaults.voices }, instructions: saved.defaults.instructions })} disabled={isDefault}>
          Reset to default
        </Button>
        {dirty && <span className="text-xs text-amber-700">Unsaved: interviews and steps 2–3 still use the saved voice.</span>}
        {justSaved && !dirty && (
          <span className="text-xs text-[color:var(--text-secondary)]">
            Saved. Now <Link to="/admin/standard-questions" className="underline">regenerate the Standard question audio</Link>; until then those questions are generated live in the new voice.
          </span>
        )}
      </div>
      <audio ref={audioRef} className="hidden" />
    </section>
  );
}

// ── Step 1b: compare ElevenLabs voices (VOICE_PLAN.md, 2026-10-30) ─────────

const SETTING_LABELS = { stability: "Stability", similarity_boost: "Similarity", style: "Style", speed: "Speed" };
const KIND_LABELS = { welcome: "Welcome", question: "Question", "follow-up": "Follow-up", next: "Next question", repeat: "Repeat" };

function voiceLabel(v) {
  const tags = [v.gender, v.accent, v.age].filter(Boolean).join(", ");
  return tags ? `${v.name} (${tags})` : v.name;
}

function ElevenLabsCard({ saved, setSaved }) {
  const [opts, setOpts] = useState(null); // null = loading
  const [model, setModel] = useState("");
  const [voices, setVoices] = useState({ female: "", male: "" });
  const [names, setNames] = useState({ female: "", male: "" }); // typed names for pasted voice IDs
  const [settings, setSettings] = useState({});
  const [testVoice, setTestVoice] = useState("female");
  const [clips, setClips] = useState({}); // line index -> { status, ms, error, data }
  const [previewed, setPreviewed] = useState({ female: false, male: false });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/admin/elevenlabs/options", { timeout: 30000 })
      .then(({ data }) => {
        setOpts(data);
        const el = data.current?.elevenlabs || {};
        setModel(el.model_id || data.models?.[0]?.model_id || "");
        setVoices({ female: el.voices?.female || "", male: el.voices?.male || "" });
        setNames({ female: el.names?.female || "", male: el.names?.male || "" });
        setSettings(el.settings || {});
      })
      .catch((e) => setOpts({ configured: false, error: formatApiErrorDetail(e), test_set: [] }));
  }, []);

  // Any change to the model, voices or settings makes the clips and previews out of date.
  const changed = (fn) => { fn(); setClips({}); setPreviewed({ female: false, male: false }); };

  if (!opts) return null;
  const active = saved?.provider === "elevenlabs";
  const ready = opts.configured && !opts.error;
  const manual = Boolean(opts.manual_voices);
  const nameFor = (g) => opts.voices?.find((v) => v.voice_id === voices[g])?.name || names[g].trim() || voices[g];
  const voiceId = voices[testVoice];

  const makeLine = async (i, play) => {
    setClips((c) => ({ ...c, [i]: { status: "making" } }));
    try {
      const { data } = await api.post("/admin/elevenlabs/clip", {
        model_id: model, voice_id: voiceId, settings, text: opts.test_set[i].text,
      }, { timeout: 45000 });
      setClips((c) => ({ ...c, [i]: { status: "ready", ms: data.generation_ms, data } }));
      setPreviewed((p) => ({ ...p, [testVoice]: true }));
      if (play) await playClip(audioRef.current, data);
      return data;
    } catch (e) {
      setClips((c) => ({ ...c, [i]: { status: "error", error: formatApiErrorDetail(e) } }));
      return null;
    }
  };

  const playLine = async (i) => {
    const clip = clips[i];
    if (clip?.status === "ready") await playClip(audioRef.current, clip.data);
    else await makeLine(i, true);
  };

  // Makes the whole set one clip at a time, so each generation time is real.
  const makeAll = async () => {
    setBusy(true);
    for (let i = 0; i < opts.test_set.length; i++) await makeLine(i, false);
    setBusy(false);
  };

  const use = async () => {
    if (!window.confirm(`Make ElevenLabs the interview voice for all new interview audio?\nFemale: ${nameFor("female")}\nMale: ${nameFor("male")}\nSaved question audio will need regenerating.`)) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/interview-voice/provider", {
        provider: "elevenlabs",
        elevenlabs: { model_id: model, voices, names: { female: nameFor("female"), male: nameFor("male") }, settings },
      });
      setSaved(data);
      toast.success("ElevenLabs is now the interview voice.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const backToOpenAI = async () => {
    if (!window.confirm("Switch the interview voice back to OpenAI? Saved question audio will need regenerating.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/interview-voice/provider", { provider: "openai" });
      setSaved(data);
      toast.success("OpenAI is the interview voice again.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const madeMs = Object.values(clips).filter((c) => c.status === "ready").map((c) => c.ms);
  const canUse = ready && model && voices.female && voices.male && previewed.female && previewed.male;

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4" data-testid="elevenlabs-card">
      <StepTitle number="1b" title={`Compare ElevenLabs voices${active ? " (the interview voice now)" : ""}`}>
        Choose a model and a voice from your ElevenLabs account, then play the fixed test set. Each clip shows how long it took to make.
        "Use this voice" needs at least one clip played in both the female and the male voice.
      </StepTitle>
      {!ready ? (
        <p className="text-sm text-amber-700" data-testid="elevenlabs-not-ready">
          {opts.configured ? opts.error : "Key not set up yet: add ELEVENLABS_API_KEY (encrypted) in DigitalOcean, then reload this page. Interviews keep using the OpenAI voice."}
        </p>
      ) : (
        <>
          {(opts.notes || []).map((n) => (
            <p key={n} className="text-xs text-[color:var(--text-secondary)]" data-testid="elevenlabs-note">{n}</p>
          ))}
          <div className="flex flex-wrap gap-6">
            <label className="text-xs font-medium">
              Model
              <select value={model} onChange={(e) => changed(() => setModel(e.target.value))}
                className="mt-1 block rounded-lg border border-[color:var(--border-stone)] px-2 py-1 text-sm font-normal" data-testid="elevenlabs-model">
                {opts.models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
              </select>
            </label>
            {manual && ["female", "male"].map((g) => (
              <div key={g} className="text-xs font-medium space-y-1">
                <div>{VOICE_LABELS[g]} voice</div>
                <input value={voices[g]} placeholder="Voice ID (Copy ID in ElevenLabs)" maxLength={64}
                  onChange={(e) => changed(() => setVoices((v) => ({ ...v, [g]: e.target.value.trim() })))}
                  className="block w-64 rounded-lg border border-[color:var(--border-stone)] px-2 py-1 text-sm font-normal" data-testid={`elevenlabs-voice-id-${g}`} />
                <input value={names[g]} placeholder="Name, e.g. Sarah" maxLength={80}
                  onChange={(e) => setNames((n) => ({ ...n, [g]: e.target.value }))}
                  className="block w-64 rounded-lg border border-[color:var(--border-stone)] px-2 py-1 text-sm font-normal" />
              </div>
            ))}
            {!manual && ["female", "male"].map((g) => (
              <label key={g} className="text-xs font-medium">
                {VOICE_LABELS[g]} voice
                <select value={voices[g]} onChange={(e) => changed(() => setVoices((v) => ({ ...v, [g]: e.target.value })))}
                  className="mt-1 block max-w-xs rounded-lg border border-[color:var(--border-stone)] px-2 py-1 text-sm font-normal" data-testid={`elevenlabs-voice-${g}`}>
                  <option value="">Choose…</option>
                  {opts.voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{voiceLabel(v)}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-6">
            {Object.entries(opts.setting_ranges || {}).map(([key, [lo, hi]]) => (
              <label key={key} className="text-xs font-medium">
                {SETTING_LABELS[key] || key}: {settings[key] ?? "default"}
                <input type="range" min={lo} max={hi} step={0.05} value={settings[key] ?? (lo + hi) / 2}
                  onChange={(e) => changed(() => setSettings((s) => ({ ...s, [key]: Number(e.target.value) })))}
                  className="mt-1 block w-36" />
              </label>
            ))}
            <Button onClick={() => changed(() => setSettings({}))} disabled={!Object.keys(settings).length}>Default settings</Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 border-t border-[color:var(--border-stone)] pt-4 text-sm">
            <span className="text-xs font-medium">Test set in:</span>
            {["female", "male"].map((g) => (
              <label key={g} className="flex items-center gap-2">
                <input type="radio" name="el-test-voice" checked={testVoice === g}
                  onChange={() => { setTestVoice(g); setClips({}); }} />
                {VOICE_LABELS[g]}{voices[g] ? ` (${nameFor(g)})` : ""}{previewed[g] ? " ✓" : ""}
              </label>
            ))}
            <Button onClick={makeAll} disabled={busy || !model || !voiceId} data-testid="elevenlabs-make-all">
              {busy ? "Making…" : "Make all clips"}
            </Button>
            {madeMs.length > 0 && (
              <span className="text-xs text-[color:var(--text-secondary)]">
                Average {(madeMs.reduce((a, b) => a + b, 0) / madeMs.length / 1000).toFixed(1)} s, slowest {(Math.max(...madeMs) / 1000).toFixed(1)} s
              </span>
            )}
          </div>
          <table className="w-full text-sm" data-testid="elevenlabs-test-set">
            <tbody>
              {opts.test_set.map((line, i) => {
                const c = clips[i] || {};
                return (
                  <tr key={i} className="border-t border-[color:var(--border-stone)] align-top">
                    <td className="py-2 pr-3 text-xs text-[color:var(--text-secondary)] whitespace-nowrap">{KIND_LABELS[line.kind] || line.kind}</td>
                    <td className="py-2 pr-3">{line.text}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <Button onClick={() => playLine(i)} disabled={busy || c.status === "making" || !model || !voiceId}>
                        {c.status === "making" ? "Making…" : "▶ Play"}
                      </Button>
                    </td>
                    <td className="py-2 text-xs whitespace-nowrap">
                      {c.status === "ready" && <span className="text-[color:var(--text-secondary)]">{(c.ms / 1000).toFixed(1)} s</span>}
                      {c.status === "error" && <span className="text-rose-700" title={c.error}>Failed</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {ready && (
          <Button variant="primary" onClick={use} disabled={saving || !canUse} data-testid="elevenlabs-use">
            {saving ? "Saving…" : "Use this voice"}
          </Button>
        )}
        {active && (
          <Button onClick={backToOpenAI} disabled={saving} data-testid="elevenlabs-back-to-openai">Switch back to OpenAI</Button>
        )}
        {active && (
          <span className="text-xs text-[color:var(--text-secondary)]">
            Now: {saved.elevenlabs?.names?.female || saved.elevenlabs?.voices?.female} / {saved.elevenlabs?.names?.male || saved.elevenlabs?.voices?.male}, model {saved.elevenlabs?.model_id}.
          </span>
        )}
      </div>
      <audio ref={audioRef} className="hidden" />
    </section>
  );
}

// ── Step 1c: the voice sample on the setup page (2026-10-30) ───────────────
// Stored on its own, never in the Standard Question library, so the question
// generator can't use it in an interview.

function VoiceSampleCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(() => api.get("/admin/voice-sample")
    .then(({ data: d }) => { setData(d); setDraft(d.text || ""); })
    .catch((e) => toast.error(formatApiErrorDetail(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const dirty = draft.trim() !== (data.text || "");

  const save = async () => {
    setBusy("save");
    try {
      const { data: d } = await api.put("/admin/voice-sample", { text: draft });
      setData(d);
      setDraft(d.text || "");
      toast.success(d.text ? "Voice sample saved. Play each voice to make its audio." : "Voice sample removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(null);
    }
  };

  const play = async (voice) => {
    setBusy(voice);
    try {
      const { data: d } = await api.post(`/admin/voice-sample/${voice}`, {}, { timeout: 45000 });
      await playClip(audioRef.current, d);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(null);
    }
  };

  const status = (voice) => {
    const a = data.audio?.[voice] || {};
    if (a.current) return "saved";
    return a.saved ? "out of date: made again on the next play" : "not made yet: made on the first play";
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3" data-testid="voice-sample-card">
      <StepTitle number="1c" title="Voice sample on the setup page">
        Candidates hear this phrase when they press "Hear this voice" next to the female and male voice choice.
        It is stored on its own, never in the Standard Questions library, so it is never asked in an interview.
        Leave it empty to hide the button.
      </StepTitle>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={data.max_chars}
        rows={3}
        placeholder="e.g. Hello, and welcome. I'll be your interviewer today."
        className="w-full rounded-lg border border-[color:var(--border-stone)] p-2 text-sm"
        data-testid="voice-sample-text"
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={save} disabled={!dirty || Boolean(busy)} data-testid="voice-sample-save">
          {busy === "save" ? "Saving…" : "Save phrase"}
        </Button>
        {["female", "male"].map((g) => (
          <span key={g} className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
            <Button onClick={() => play(g)} disabled={!data.text || dirty || Boolean(busy)} data-testid={`voice-sample-play-${g}`}>
              {busy === g ? "Making…" : `▶ ${VOICE_LABELS[g]}`}
            </Button>
            {data.text ? status(g) : ""}
          </span>
        ))}
        {dirty && <span className="text-xs text-amber-700">Save the phrase before playing it.</span>}
      </div>
      <audio ref={audioRef} className="hidden" />
    </section>
  );
}

// ── Step 3: the interviewer's fixed phrases ─────────────────────────────────

const PHRASE_FIELDS = [
  { key: "welcome_with_name", label: "Welcome, with the first name", hint: "{name} is replaced with the candidate's first name from their account." },
  { key: "welcome_without_name", label: "Welcome, when there's no first name" },
  { key: "next.0", label: "After an answer — phrase 1", hint: "The three are used in turn: 1, 2, 3, 1, 2, 3…" },
  { key: "next.1", label: "After an answer — phrase 2" },
  { key: "next.2", label: "After an answer — phrase 3" },
  { key: "end", label: "End of the interview" },
  { key: "lead_in.0", label: "Before a follow-up — lead-in 1", hint: "Said just before a follow-up question. The three are used in turn." },
  { key: "lead_in.1", label: "Before a follow-up — lead-in 2" },
  { key: "lead_in.2", label: "Before a follow-up — lead-in 3" },
  { key: "repeat", label: "Asking to repeat an unclear answer", hint: "Said once per question when an answer can't be understood." },
  { key: "closing_question", label: "The closing question", hint: "Asked after Question 10 in paid and beta interviews (not the Free trial)." },
  { key: "closing_reply", label: "Reply when the candidate asks questions", hint: "Neutral: the interviewer never answers or judges the questions; the report gives the feedback." },
  { key: "closing_reply_none", label: "Reply when the candidate has no questions", hint: "Followed by the end of the interview." },
  { key: "end_after_closing_with_name", label: "End of the interview, after the closing question, with the first name", hint: "Said straight after either reply above, so it has no \"Thank you\". {name} is replaced with the candidate's first name from their account." },
  { key: "end_after_closing", label: "End of the interview, after the closing question, without a name", hint: "Used when the account has no first name." },
];
// Phrases that must say the first name: {name} exactly once.
const NAME_FIELDS = ["welcome_with_name", "end_after_closing_with_name"];
const hasOneName = (text) => ((text || "").match(/\{[^}]*\}/g) || []).join() === "{name}";

// "next.1" → ["next", 1]; "end" → ["end", null]
function splitKey(key) {
  const [name, n] = key.split(".");
  return [name, n === undefined ? null : Number(n)];
}

function getPhrase(phrases, key) {
  const [name, n] = splitKey(key);
  if (n !== null) return phrases[name]?.[n] ?? "";
  return phrases[name] ?? "";
}

function setPhrase(phrases, key, value) {
  const [name, n] = splitKey(key);
  if (n !== null) {
    const list = [...(phrases[name] || ["", "", ""])];
    list[n] = value;
    return { ...phrases, [name]: list };
  }
  return { ...phrases, [name]: value };
}

function samePhrases(a, b) {
  return PHRASE_FIELDS.every((f) => getPhrase(a, f.key) === getPhrase(b, f.key));
}

function phrasesOnly(p) {
  return {
    welcome_with_name: p.welcome_with_name,
    welcome_without_name: p.welcome_without_name,
    next: [...p.next],
    end: p.end,
    lead_in: [...(p.lead_in || [])],
    repeat: p.repeat,
    closing_question: p.closing_question,
    closing_reply: p.closing_reply,
    closing_reply_none: p.closing_reply_none,
    end_after_closing: p.end_after_closing,
    end_after_closing_with_name: p.end_after_closing_with_name,
  };
}

function InterviewerPhrasesCard({ draft, setDraft, saved, setSaved, voiceCheckOn, voiceNames }) {
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [testName, setTestName] = useState("Thaís");
  // Interviews speak these phrases in the candidate's chosen voice; preview either one.
  const [previewVoice, setPreviewVoice] = useState("female");
  const previewRef = useRef(null);
  if (!saved || !draft) return null;
  const dirty = !samePhrases(draft, saved);
  const allFilled = PHRASE_FIELDS.every((f) => getPhrase(draft, f.key).trim());

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/spoken-phrases", phrasesOnly(draft));
      setSaved(data);
      setDraft(phrasesOnly(data));
      toast.success("Interviewer phrases saved. New interviews use them from now on.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  // Every fixed phrase for both voices, made now in the background (they are also
  // remade after each save here), so no interview waits while one is made.
  const prepareClips = async () => {
    setPreparing(true);
    try {
      await api.post("/admin/interview-voice/prepare-clips");
      toast.success("Making every interviewer phrase for both voices. It takes about a minute.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPreparing(false);
    }
  };

  const preview = async (key) => {
    setPreviewKey(key);
    try {
      const { data } = key === "welcome_with_name"
        ? await api.post("/admin/voice-check/clip", {
          name: testName.trim() || "Maria", voice: previewVoice, with_name: draft.welcome_with_name,
        }, { timeout: 45000 })
        : await api.post("/admin/voice-check/phrase", {
          text: getPhrase(draft, key).replace("{name}", testName.trim() || "Maria"), voice: previewVoice,
        }, { timeout: 45000 });
      await playClip(previewRef.current, data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPreviewKey(null);
    }
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3" data-testid="interviewer-phrases-card">
      <StepTitle number={3} title="Interviewer phrases">
        What the interviewer says at the start, after each answer, before a follow-up and at the end, previewed in the saved interview voice.
        Saving changes them for new interviews straight away.
      </StepTitle>
      {PHRASE_FIELDS.map((f) => {
        const value = getPhrase(draft, f.key);
        const invalid = NAME_FIELDS.includes(f.key) ? !hasOneName(value) : !value.trim();
        return (
          <div key={f.key} className="space-y-1">
            <label className="block text-xs font-medium">
              {f.label}
              <div className="mt-1 flex items-center gap-2">
                <input
                  value={value}
                  onChange={(e) => setDraft((d) => setPhrase(d, f.key, e.target.value))}
                  maxLength={saved.max_chars}
                  className={`w-full rounded-lg border p-2 text-sm font-normal ${invalid ? "border-rose-400" : "border-[color:var(--border-stone)]"}`}
                  data-testid={`phrase-${f.key}`}
                />
                {voiceCheckOn && (
                  <Button onClick={() => preview(f.key)} disabled={Boolean(previewKey) || invalid}>
                    {previewKey === f.key ? "Making…" : "▶ Preview"}
                  </Button>
                )}
              </div>
            </label>
            {f.hint && <div className="text-[11px] text-[color:var(--text-secondary)]">{f.hint}</div>}
            {NAME_FIELDS.includes(f.key) && !hasOneName(value) && <div className="text-[11px] text-rose-700">Must contain {"{name}"} exactly once.</div>}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        {voiceCheckOn && (
          <label className="flex items-center gap-2 text-xs">
            Preview in
            <select
              value={previewVoice}
              onChange={(e) => setPreviewVoice(e.target.value)}
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs"
              data-testid="phrases-preview-voice"
            >
              {["female", "male"].map((g) => (
                <option key={g} value={g}>{VOICE_LABELS[g]}{voiceNames?.[g] ? ` (${voiceNames[g]})` : ""}</option>
              ))}
            </select>
          </label>
        )}
        {voiceCheckOn && (
          <label className="flex items-center gap-2 text-xs">
            Test name for the previews with a name
            <input value={testName} onChange={(e) => setTestName(e.target.value)} className="w-32 rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs" />
          </label>
        )}
        <Button variant="primary" onClick={save} disabled={saving || !dirty || !NAME_FIELDS.every((k) => hasOneName(draft[k])) || !allFilled} data-testid="phrases-save">
          {saving ? "Saving…" : "Save phrases"}
        </Button>
        <Button onClick={() => setDraft(phrasesOnly(saved.defaults))} disabled={samePhrases(draft, saved.defaults)}>
          Reset to defaults
        </Button>
        <Button onClick={prepareClips} disabled={preparing} data-testid="phrases-prepare">
          {preparing ? "Starting…" : "Prepare all clips now"}
        </Button>
        {dirty && <span className="text-xs text-amber-700">Unsaved changes. Step 2 previews the edited welcome.</span>}
      </div>
      <audio ref={previewRef} className="hidden" />
    </section>
  );
}

// ── Follow-up questions on or off ────────────────────────────────────────────

// ── Advanced Mode wait times (2026-11-04) ─────────────────────────────────────

const secs = (ms) => (typeof ms === "number" ? `${(ms / 1000).toFixed(1)} s` : "—");

function WaitTimesCard() {
  const [sessionId, setSessionId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const id = sessionId.trim();
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/interviews/${encodeURIComponent(id)}/wait-times`);
      setResult(data);
    } catch (e) {
      setResult(null);
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3" data-testid="wait-times-card">
      <div>
        <h2 className="text-sm font-semibold">Advanced Mode wait times</h2>
        <p className="text-xs text-[color:var(--text-secondary)] mt-1">
          For one Advanced Mode interview: how long the candidate waited after each answer until the interviewer spoke again.
          Transcription and server time are part of the wait. Interviews from before 4 November 2026 have no times.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Interview ID, e.g. iv_0fd843e85d2a"
          className="w-72 rounded-lg border border-[color:var(--border-stone)] p-2 text-sm"
          data-testid="wait-times-id"
        />
        <Button onClick={load} disabled={loading || !sessionId.trim()} data-testid="wait-times-load">
          {loading ? "Loading…" : "Show wait times"}
        </Button>
      </div>
      {result && (result.rows.length === 0 ? (
        <p className="text-xs text-[color:var(--text-secondary)]">No wait times for this interview ({result.mode === "live" ? "Advanced Mode" : "Guided Mode"}).</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs">
            Voice: {result.voice || "—"} · Average wait: <strong>{secs(result.average_ms)}</strong> · Longest: <strong>{secs(result.longest_ms)}</strong>
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[color:var(--text-secondary)]">
                <th className="py-1 pr-3">Question</th>
                <th className="py-1 pr-3">Transcription</th>
                <th className="py-1 pr-3">Server in total</th>
                <th className="py-1 pr-3">Sending and server</th>
                <th className="py-1 pr-3">Until the interviewer spoke</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r, n) => (
                <tr key={n} className="border-t border-[color:var(--border-stone)]">
                  <td className="py-1 pr-3">{(r.question_index ?? 0) + 1}{r.follow_up ? " (follow-up)" : ""}</td>
                  <td className="py-1 pr-3">{secs(r.transcribe_ms)}</td>
                  <td className="py-1 pr-3">{secs(r.server_ms)}</td>
                  <td className="py-1 pr-3">{secs(r.send_ms)}</td>
                  <td className="py-1 pr-3 font-medium">{secs(r.wait_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

function FollowUpSwitchCard() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/follow-up-settings")
      .then(({ data }) => setEnabled(Boolean(data.enabled)))
      .catch((e) => toast.error(formatApiErrorDetail(e)));
  }, []);
  if (enabled === null) return null;

  const toggle = async () => {
    const next = !enabled;
    if (!window.confirm(next
      ? "Turn follow-up questions ON? From the next answer, every interview checks each answer and may ask one follow-up."
      : "Turn follow-up questions OFF? Interviews go back to moving on after every answer.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/follow-up-settings", { enabled: next });
      setEnabled(Boolean(data.enabled));
      toast.success(`Follow-up questions are ${data.enabled ? "on" : "off"}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="follow-up-switch-card">
      <div>
        <h2 className="text-sm font-semibold">Follow-up questions: {enabled ? "on" : "off"}</h2>
        <p className="text-xs text-[color:var(--text-secondary)] mt-1">
          On: after each answer the interviewer may ask one follow-up when a key point is missing, or ask the candidate to repeat an answer it couldn't understand.
          Off: interviews move on after every answer, as before.
        </p>
      </div>
      <Button variant={enabled ? undefined : "primary"} onClick={toggle} disabled={saving} data-testid="follow-up-switch">
        {saving ? "Saving…" : enabled ? "Turn off" : "Turn on"}
      </Button>
    </section>
  );
}

// ── The page ───────────────────────────────────────────────────────────────

export default function AdminVoiceCheck() {
  const [config, setConfig] = useState(null); // null = loading, false = name tests turned off
  const [voiceSaved, setVoiceSaved] = useState(null);
  const [phrasesSaved, setPhrasesSaved] = useState(null);
  const [phrasesDraft, setPhrasesDraft] = useState(null);
  const [text, setText] = useState("");
  const [voices, setVoices] = useState({ female: true, male: false });
  const [run, setRun] = useState(null); // { names, columns, jobs }
  const [results, setResults] = useState({}); // job key -> { status, url, text, voiceName, error }
  const [marks, setMarks] = useState({}); // job key -> "good" | "bad"
  const [running, setRunning] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);
  const stopAllRef = useRef(false);
  const urlsRef = useRef([]);

  useEffect(() => {
    api.get("/admin/voice-check/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(false));
    api.get("/admin/interview-voice")
      .then(({ data }) => setVoiceSaved(data))
      .catch((e) => toast.error(formatApiErrorDetail(e)));
    api.get("/admin/spoken-phrases")
      .then(({ data }) => {
        setPhrasesSaved(data);
        setPhrasesDraft(phrasesOnly(data));
      })
      .catch((e) => toast.error(formatApiErrorDetail(e)));
  }, []);

  // Release the audio when leaving the page.
  useEffect(() => () => urlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const parsed = useMemo(() => parseNames(text), [text]);
  const chosenVoices = Object.keys(voices).filter((v) => voices[v]);
  const summary = run ? summarize(run.columns, run.names, marks) : [];
  // Step 2 plays the edited welcome while it is unsaved.
  const draftWording = phrasesSaved && phrasesDraft && phrasesDraft.welcome_with_name !== phrasesSaved.welcome_with_name
    ? phrasesDraft.welcome_with_name
    : undefined;
  const voiceName = (g) => voiceSaved?.voices?.[g];

  const generate = async () => {
    if (!parsed.names.length || !chosenVoices.length) return;
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    const columns = columnsFor(chosenVoices);
    const jobs = buildJobs(parsed.names, columns);
    setRun({ names: parsed.names, columns, jobs });
    setResults(Object.fromEntries(jobs.map((j) => [j.key, { status: "waiting" }])));
    setMarks({});
    setRunning(true);
    let next = 0;
    const worker = async () => {
      while (next < jobs.length) {
        const job = jobs[next++];
        setResults((r) => ({ ...r, [job.key]: { status: "making" } }));
        try {
          const { data } = await api.post("/admin/voice-check/clip", {
            name: job.name, voice: job.voice, with_name: draftWording,
          }, { timeout: 45000 });
          const url = toObjectUrl(data.audio_base64, data.mime);
          urlsRef.current.push(url);
          setResults((r) => ({ ...r, [job.key]: { status: "ready", url, text: data.text, voiceName: data.voice_name } }));
        } catch (e) {
          setResults((r) => ({ ...r, [job.key]: { status: "error", error: formatApiErrorDetail(e) } }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker));
    setRunning(false);
  };

  const play = useCallback((key) => new Promise((resolve) => {
    const el = audioRef.current;
    const url = results[key]?.url;
    if (!el || !url) return resolve();
    const done = () => {
      el.removeEventListener("ended", done);
      el.removeEventListener("error", done);
      el.removeEventListener("pause", done);
      setPlayingKey((k) => (k === key ? null : k));
      resolve();
    };
    el.pause();
    el.src = url;
    el.addEventListener("ended", done);
    el.addEventListener("error", done);
    el.addEventListener("pause", done);
    setPlayingKey(key);
    el.play().catch(done);
  }), [results]);

  const playAll = async () => {
    if (!run) return;
    stopAllRef.current = false;
    for (const job of run.jobs) {
      if (stopAllRef.current) break;
      if (results[job.key]?.status === "ready") await play(job.key);
    }
  };

  const stop = () => {
    stopAllRef.current = true;
    audioRef.current?.pause();
  };

  const mark = (key, value) => setMarks((m) => ({ ...m, [key]: m[key] === value ? undefined : value }));

  const downloadCsv = () => {
    const blob = new Blob([toCsv(run.jobs, results, marks)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `voice-check_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const phrasesCard = (
    <InterviewerPhrasesCard
      draft={phrasesDraft}
      setDraft={setPhrasesDraft}
      saved={phrasesSaved}
      setSaved={setPhrasesSaved}
      voiceCheckOn={Boolean(config)}
      voiceNames={voiceSaved?.voices}
    />
  );

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-voice-check">
      <AdminNav title="Voice Check" />
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <FollowUpSwitchCard />
        <WaitTimesCard />
        <InterviewVoiceCard saved={voiceSaved} setSaved={setVoiceSaved} />
        <ElevenLabsCard saved={voiceSaved} setSaved={setVoiceSaved} />
        <VoiceSampleCard />

        {config === false ? (
          <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
            <StepTitle number={2} title="Test names">
              Name tests and phrase previews are turned off on this server. Set <code>VOICE_CHECK_ENABLED=true</code> to use them.
            </StepTitle>
          </section>
        ) : (
          <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4">
            <StepTitle number={2} title="Test names">
              Each name is spoken in the exact welcome the interview uses, in the saved interview voice. Nothing is stored; the clips exist only on this page.
            </StepTitle>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={"One first name per line, e.g.\nJoão\nThaís\nGeneviève\nNguyễn\nXiaoming"}
              className="w-full rounded-lg border border-[color:var(--border-stone)] p-3 text-sm"
              data-testid="voice-check-names"
            />
            <div className="text-xs text-[color:var(--text-secondary)]">
              {parsed.names.length} name(s){parsed.truncated ? `; only the first ${MAX_NAMES} are used (${parsed.truncated} left out)` : ""}
              {parsed.rejected.length ? `; not a first name, left out: ${parsed.rejected.join(", ")}` : ""}
            </div>
            <div className="flex flex-wrap items-center gap-5 text-sm">
              {Object.keys(VOICE_LABELS).map((v) => (
                <label key={v} className="flex items-center gap-2">
                  <input type="checkbox" checked={voices[v]} onChange={(e) => setVoices((s) => ({ ...s, [v]: e.target.checked }))} />
                  {VOICE_LABELS[v]} voice{voiceName(v) ? ` (${voiceName(v)})` : ""}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={generate} disabled={running || !parsed.names.length || !chosenVoices.length} data-testid="voice-check-generate">
                {running ? "Making clips…" : "Generate"}
              </Button>
              {run && (
                <span className="text-xs text-[color:var(--text-secondary)]">
                  {Object.values(results).filter((r) => r.status === "ready").length} of {run.jobs.length} clips ready
                </span>
              )}
            </div>

            {run && (
              <div className="space-y-4 border-t border-[color:var(--border-stone)] pt-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={playAll} disabled={!Object.values(results).some((r) => r.status === "ready")}>▶ Play all</Button>
                  <Button onClick={stop}>■ Stop</Button>
                  <Button onClick={downloadCsv}>Download CSV</Button>
                  <div className="text-xs text-[color:var(--text-secondary)] flex flex-wrap gap-4">
                    {summary.map((s) => (
                      <span key={s.key}>
                        {VOICE_LABELS[s.voice]}: <strong>{s.good}</strong> of {s.marked} marked sound right
                      </span>
                    ))}
                  </div>
                </div>
                <audio ref={audioRef} className="hidden" />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="voice-check-results">
                    <thead>
                      <tr className="text-left text-xs text-[color:var(--text-secondary)]">
                        <th className="py-2 pr-4">Name</th>
                        {run.columns.map((c) => (
                          <th key={c.key} className="py-2 pr-4">{VOICE_LABELS[c.voice]}{voiceName(c.voice) ? ` (${voiceName(c.voice)})` : ""}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {run.names.map((name) => (
                        <tr key={name} className="border-t border-[color:var(--border-stone)]">
                          <td className="py-2 pr-4 font-medium">{name}</td>
                          {run.columns.map((c) => {
                            const key = `${name}|${c.key}`;
                            const r = results[key] || {};
                            return (
                              <td key={key} className="py-2 pr-4">
                                {r.status === "ready" ? (
                                  <div className="flex items-center gap-2">
                                    <Button onClick={() => play(key)}>{playingKey === key ? "♪ Playing" : "▶ Play"}</Button>
                                    <Button onClick={() => mark(key, "good")} aria-pressed={marks[key] === "good"}
                                      className={`rounded-full border px-2 py-1 text-xs ${marks[key] === "good" ? "bg-emerald-100 border-emerald-400" : "border-[color:var(--border-stone)]"}`}
                                      title="Sounds right">✓</Button>
                                    <Button onClick={() => mark(key, "bad")} aria-pressed={marks[key] === "bad"}
                                      className={`rounded-full border px-2 py-1 text-xs ${marks[key] === "bad" ? "bg-rose-100 border-rose-400" : "border-[color:var(--border-stone)]"}`}
                                      title="Mispronounced">✗</Button>
                                  </div>
                                ) : r.status === "error" ? (
                                  <span className="text-xs text-rose-700" title={r.error}>Failed</span>
                                ) : (
                                  <span className="text-xs text-[color:var(--text-secondary)]">{r.status === "making" ? "Making…" : "Waiting"}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {phrasesCard}
      </main>
    </div>
  );
}
